/* eslint-disable camelcase */

import Button from '@/shared/Button'
import FormCreator from '@/shared/FormCreator'

import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'

import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import * as Yup from 'yup'

const mapStateToProps = ({ loading }) => ({
  isLoading: loading['account/resetPassword'],
})

const inputConfig = {
  variant: 'standard',
  inputBaseProps: {
    labelSuffix: '',
    justifyContent: 'stretch',
    labelProps: {
      sx: {
        color: theme => theme.palette.btn_positive.main,
        width: 100,
        justifyContent: 'flex-end',
      },
    },
    childrenSx: { flex: 1 },
  },
  color: 'btn_positive',
}

const ChangePasswordDialog = ({ dispatch, isLoading, onClose, onComplete, ...props }) => {
  Yup.addMethod(Yup.string, 'checkPassword', function testCheckPassword(errorMessage) {
    return this.test('test-check-password', errorMessage, (_, context) => {
      const { parent = {}, path, createError } = context
      const { new_password = '' } = parent

      let checkCount = 0

      if (new_password.match(/(?=.*[a-z])/)) {
        checkCount += 1
      }

      if (new_password.match(/(?=.*[A-Z])/)) {
        checkCount += 1
      }

      if (new_password.match(/(?=.*\d)/)) {
        checkCount += 1
      }

      if (new_password.match(/(?=.*\W)/)) {
        checkCount += 1
      }

      if (new_password.match(/(?=.* )/)) {
        checkCount = 0
      }

      if (checkCount < 3) {
        return createError({ path, message: errorMessage })
      }
      return true
    })
  })

  const form = useRef()
  const { t } = useTranslation()

  const handleClose = () => {
    onClose?.()
  }

  const handleSubmit = () => {
    const { handleSubmit: formSubmit } = form.current

    formSubmit?.(values => {
      const { old_password, new_password } = values

      dispatch({
        type: 'account/resetPassword',
        payload: { orginPwd: old_password, newPwd: new_password },
        successCallback: () => {
          dispatch({
            type: 'app/notify',
            payload: { message: t('messages.password_reset'), severity: 'success' },
          })
        },
        errorCallback: err => {
          dispatch({
            type: 'app/notify',
            payload: { message: err, severity: 'error' },
          })
        },
      })
    })?.()
  }

  return (
    <Dialog {...props} maxWidth={'md'}>
      <DialogTitle
        sx={{
          textAlign: 'center',
          fontWeight: 'bold',
          color: theme => theme.palette.primary.main,
        }}
      >
        {t('general.change_password')}
      </DialogTitle>
      <DialogContent sx={{ width: 650, pr: 8 }}>
        <FormCreator
          ref={form}
          gridItemProps={{ md: 3 }}
          formItems={[
            {
              label: t('general.old_password'),
              name: 'old_password',
              validator: Yup.string().required(t('validation.password_required')),
              defaultValue: '',
              type: 'password',
              placeholder: t('general.input_password_format'),
              inputConfig: {
                ...inputConfig,
              },
            },
            {
              label: t('general.new_password'),
              name: 'new_password',
              validator: Yup.string()
                .required(t('validation.new_password_required'))
                .min(8, t('validation.new_password_check'))
                .checkPassword(t('validation.new_password_check')),
              defaultValue: '',
              type: 'password',
              placeholder: t('general.input_password_format'),
              inputConfig: {
                ...inputConfig,
              },
            },
            {
              label: t('general.confirm_new_password'),
              name: 're_new_password',
              validator: Yup.string()
                .oneOf([Yup.ref('new_password'), null], t('validation.new_password_mismatch'))
                .required(t('validation.new_password_required'))
                .min(8, t('validation.new_password_check'))
                .checkPassword(t('validation.new_password_check')),
              defaultValue: '',
              type: 'password',
              placeholder: t('general.input_password_format'),
              inputConfig: {
                ...inputConfig,
              },
            },
          ]}
          submitComponent={() => null}
          resetComponent={() => null}
        />
      </DialogContent>
      <DialogActions sx={{ alignSelf: 'center' }}>
        <Button
          sx={{ minWidth: 150, marginRight: '30px' }}
          loading={isLoading}
          disabled={isLoading}
          onClick={handleClose}
        >
          {t('general.cancel')}
        </Button>
        <Button
          sx={{ minWidth: 150 }}
          loading={isLoading}
          disabled={isLoading}
          onClick={async () => {
            handleSubmit()
          }}
        >
          {t('general.complete')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default connect(mapStateToProps)(ChangePasswordDialog)
